import * as React from 'react';
import { Button, Modal, Select } from '@teamsnap/teamsnap-ui';
import './delete-participant-modal.scss';
import { Member } from 'models';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import SeasonParticipant from 'models/season-participant';
import { MemberToDelete, deleteSeasonParticipants } from '../season-participants-service';
import { displayFirstNameLast } from 'core/utils';

type Props = {
  members: Member[];
  show: boolean;
  setShow: (x: boolean) => void;
  whenFinishDelete?: () => void;
  onDelete: (member) => void;
};

const nonDeletableRoles = ['Commissioner', 'League Owner', 'Team Owner'];

export const canDeleteParticipation = (participation: SeasonParticipant) =>
  !participation.participationRoles ||
  participation.participationRoles.every((x) => !nonDeletableRoles.includes(x.name));

const showFeedback = (success: boolean, members?: Member[]) => {
  reactiveFeedbackMessage({
    autoclose: success,
    isError: !success,
    message: success
      ? members.length === 1
        ? `${displayFirstNameLast(members[0])} deleted`
        : `${members.length} Participants Deleted`
      : 'Failed to delete participant. Try again',
  });
};

const getWarningMessage = (members: MemberToDelete[]) => {
  if (members.length === 1) {
    const name = displayFirstNameLast(members[0]);
    return members[0].deletableParticipations.length === 1
      ? `Are you sure you want to delete ${name}? This will delete all participant information for this season`
      : `Which ${name} profile would you like to delete?`;
  }

  return `Are you sure you wish to delete ${members.length} participants? This will delete all of their roles and participant information for this season.`;
};

const getErrorMessage = (members: MemberToDelete[]) => (
  <>
    Commissioners, League Owners and Team Owners can not be deleted!
    {members.length > 1 && (
      <>
        {' Please de-select the following participant(s) or delete their roster profiles individually:'}
        {members
          .filter((x) => !x.canBeDeleted)
          .map((member) => (
            <>
              <br />
              {displayFirstNameLast(member)}
            </>
          ))}
      </>
    )}
  </>
);

const ALL_PARTICIPATIONS = 0;

const DeleteModal = ({ show, setShow, members, whenFinishDelete, onDelete }: Props) => {
  const [participationSelected, setParticipationSelected] = React.useState(ALL_PARTICIPATIONS);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const { membersToDelete, allMembersCanBeDeleted } = members.reduce(
    (prev, member) => {
      const deletableParticipations = member.seasonParticipants?.filter(canDeleteParticipation);

      const participationsToDelete =
        participationSelected === ALL_PARTICIPATIONS
          ? deletableParticipations
          : member.seasonParticipants?.filter((x) => x.seasonParticipantId === participationSelected);

      const canBeDeleted =
        deletableParticipations && deletableParticipations.length === member.seasonParticipants?.length;

      return {
        membersToDelete: [
          ...prev.membersToDelete,
          {
            ...member,
            canBeDeleted,
            deletableParticipations,
            participationsToDelete: participationsToDelete?.map(({ seasonParticipantId, seasonParticipantType }) => ({
              seasonParticipantId,
              seasonParticipantType,
            })),
          },
        ],
        allMembersCanBeDeleted: prev.allMembersCanBeDeleted && canBeDeleted,
      };
    },
    { membersToDelete: [], allMembersCanBeDeleted: true }
  );

  const enableDeleteButton =
    membersToDelete.length === 1 ? membersToDelete[0]?.deletableParticipations?.length > 0 : allMembersCanBeDeleted;

  const handleClick = async () => {
    try {
      setIsDeleting(true);
      onDelete(membersToDelete);
      setParticipationSelected(ALL_PARTICIPATIONS);
      setIsDeleting(false);
      showFeedback(true, members);
      whenFinishDelete && whenFinishDelete();
      setShow(false);
    } catch (e) {
      setIsDeleting(false);
      showFeedback(false);
    }
  };

  return (
    <Modal
      heading="Delete Participant?"
      style={{ minWidth: '0', width: '451px', textAlign: 'start' }}
      show={show}
      showClose
      allowOverlayClose
      closeFn={() => setShow(false)}
    >
      <div>
        <div>
          <p className="u-textLeft">
            {enableDeleteButton ? getWarningMessage(membersToDelete) : getErrorMessage(membersToDelete)}
          </p>
          {membersToDelete.length === 1 && membersToDelete[0].deletableParticipations.length > 1 && (
            <Select
              name="from"
              mods="u-spaceTopMd"
              options={[
                { value: ALL_PARTICIPATIONS, label: 'All Profiles' },
                ...membersToDelete[0].deletableParticipations.map((x) => ({
                  value: x.seasonParticipantId,
                  label: `${x.team?.name ?? 'No Team'} (${x.division?.name ?? 'No Division'})`,
                })),
              ]}
              inputProps={{
                value: participationSelected,
                onChange: (e) => setParticipationSelected(parseInt(e.target.value)),
              }}
            />
          )}
          <div className="buttons-wrapper">
            <Button mods="u-spaceRightSm" onClick={() => setShow(false)}>
              Cancel
            </Button>
            <Button
              mods="Button Button--primary u-bgNegative u-borderColorNegative"
              onClick={handleClick}
              isDisabled={!enableDeleteButton || isDeleting}
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

DeleteModal.defaultProps = {
  whenFinishDelete: null,
};

export default DeleteModal;
